// ./src/pages/private/EmbeddingPreviewPage/EmbeddingPreviewPage.tsx
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Stack, Title, Text, Paper, Code, Alert } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";

import { SimpleLayout } from "@/components/layouts";
import { AppButton } from "@/components/generic";
import { useEmbeddings } from "@/hooks";
import { useApp } from "@/contexts";

export const EmbeddingPreviewPage: React.FC<PageProps> = (props: PageProps) => {
  const { embeddingId } = useParams<{ embeddingId: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useApp();

  const { embedding, loading, get, lastError } = useEmbeddings({
    autoLoad: false,
    isAuthenticated,
  });

  // Load embedding data on mount
  useEffect(() => {
    if (embeddingId) {
      get(embeddingId);
    }
  }, [embeddingId, get]);

  // Load the embedding script after the embedding data is loaded
  useEffect(() => {
    if (!embedding?.accessToken) return;

    const scriptUrl = `${import.meta.env.VITE_EMBEDDING_SCRIPT}?accessToken=${
      embedding.accessToken
    }&containerId=embedding-preview`;

    // Create and append the script
    const script = document.createElement("script");
    script.src = scriptUrl;
    script.async = true;
    document.body.appendChild(script);

    // Cleanup function to remove script when component unmounts
    return () => {
      document.body.removeChild(script);
    };
  }, [embedding?.accessToken]);

  const handleBack = () => {
    navigate("/embeddings");
  };

  const getEmbedCodeSnippet = () => {
    if (!embedding?.accessToken) return "";

    return `<div id="events"></div>
<script src="${import.meta.env.VITE_EMBEDDING_SCRIPT}?accessToken=${
      embedding.accessToken
    }&containerId=events"></script>`;
  };

  return (
    <SimpleLayout {...props}>
      <Stack gap="md">
        {/* Error state */}
        {lastError && (
          <Alert color="red" title="Error">
            {lastError}
          </Alert>
        )}

        {/* Loading state */}
        {loading && (
          <Paper p="xl" withBorder>
            <Text>Loading embedding...</Text>
          </Paper>
        )}

        {/* Content */}
        {!loading && embedding && (
          <>
            {/* Embedding info */}
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Title order={2}>{embedding.name}</Title>
                {embedding.description && (
                  <Text c="dimmed">{embedding.description}</Text>
                )}
              </Stack>
            </Paper>

            {/* Documentation section */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={3}>How to Embed</Title>
                <Text>
                  Use the code below on your website to embed the events list
                  for this embedding.
                </Text>

                <Paper p="md" bg="gray.0" withBorder>
                  <Code block>{getEmbedCodeSnippet()}</Code>
                </Paper>

                <Text size="sm" c="dimmed">
                  Simply copy the code above and paste it into your website's
                  HTML where you want the events to appear. The events will
                  automatically load and display based on your embedding
                  configuration.
                </Text>
              </Stack>
            </Paper>

            {/* Live preview section */}
            <Paper p="md" withBorder>
              <Stack gap="md">
                <Title order={3}>Live Preview</Title>
                <Text c="dimmed">
                  This is how your embedding will appear on your website:
                </Text>

                <Paper
                  p="md"
                  withBorder
                  style={{
                    minHeight: 200,
                    border: "2px dashed #dee2e6",
                    borderRadius: 4,
                  }}
                >
                  <div id="embedding-preview"></div>
                </Paper>
              </Stack>
            </Paper>
          </>
        )}

        {/* Not found state */}
        {!loading && !embedding && !lastError && (
          <Paper p="xl" withBorder>
            <Stack gap="md" align="center">
              <Title order={3}>Embedding Not Found</Title>
              <Text c="dimmed">
                The embedding you're looking for doesn't exist or has been
                deleted.
              </Text>
              <AppButton onClick={handleBack}>Back to Embeddings</AppButton>
            </Stack>
          </Paper>
        )}
      </Stack>
    </SimpleLayout>
  );
};
